import {
  Grid,
  GridItem,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
  Link,
  Box,
  chakra,
  Button,
  Icon,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  Stack,
  RadioGroup,
  BoxProps,
  useColorModeValue
} from '@chakra-ui/react';
import * as React from 'react';
import LogoSingleGray from '../assets/images/logo_single_gray.svg';
import LogoFull from '../assets/images/logo_full.svg';
import { BsTranslate } from 'react-icons/bs';
import { useTranslationsContext } from './useTranslations';
import LocalizedLink from './localizedLink';
import { SelectLanguageModal } from './selectLanguageModal';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { NewsletterSignup } from './newsletter';
import { warrantyTitle } from './warrantyNotice';

const ChakraLogoSingleGray = chakra(LogoSingleGray);
const ChakraLogoFull = chakra(LogoFull);

type Props = {
  locale: string;
  hideNewsletterSignup?: boolean;
} & BoxProps;

export const Footer: React.FC<Props> = ({ locale, hideNewsletterSignup = false }) => {
  const {
    opening_hours,
    monday_to_friday,
    saturday,
    sunday_and_holidays,
    only_showroom,
    email,
    phone,
    telefax,
    change_language,
    company,
    imprint,
    social_media,
    navigation_about_us,
    navigation_contact,
    navigation_cars,
    navigation_services,
    navigation_news,
    navigation_jobs,
    privacy_policy,
    information,
    tireShop,
    alt_logo_single,
    accessibility
  } = useTranslationsContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const upperBg = useColorModeValue('gray.50', 'gray.800');
  const lowerBg = useColorModeValue('gray.100', 'black');
  const lowerTextColor = useColorModeValue('gray.700', 'gray.200');
  const logoFill = useColorModeValue('black', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('black', 'white');
  const dividerColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <>
      <Box as="footer" id="footer">
        <Box w={'full'} p={['8', null, null, '16']} bgColor={upperBg}>
          <Grid
            maxW={'8xl'}
            gap={6}
            mx={'auto'}
            w={'full'}
            templateColumns={['1fr 1fr', '1fr 1fr', '2fr 1fr 1fr 1fr', '2fr 1fr 1fr 1fr']}
            position={'relative'}
          >
            <GridItem colSpan={[2, null, 1, 1]}>
              <Flex direction={'column'} align={['center', null, 'flex-start', 'flex-start']}>
                <ChakraLogoFull height={[25, null, 25, 50]} width="auto" fill={logoFill} />
                <Link
                  href="https://www.google.com/maps/search/?api=1&query=AutoCenter%20Meschede"
                  fontSize={['12px', null, null, 'sm']}
                  color={textColor}
                  fontWeight={'bold'}
                  mt={4}
                  isExternal
                >
                  AutoCenter Meschede GmbH <br />
                  Schneidweg 16
                  <br /> 59872 Meschede <ExternalLinkIcon mx="2px" />
                </Link>
                <Text mr={4} fontSize={['12px', null, null, 'sm']} color={textColor} fontWeight={'bold'}>
                  <br />
                  {opening_hours}:
                  <br />
                  {monday_to_friday} 09:00 &#45; 18:00 Uhr
                  <br />
                  {saturday} 09:00 &#45; 14:00 Uhr
                  <br />
                  {sunday_and_holidays}: 11.00 Uhr &#45; 14:00 Uhr {only_showroom}
                  <br />
                  <br />
                  {phone}: 0291 &#45; 20 25 98 0<br />
                  {telefax}: 0291 &#45; 20 25 98 31
                  <br />
                  {email}: info@autocenter-meschede.de
                </Text>
                <Button
                  fontWeight={'bold'}
                  color={textColor}
                  mt={4}
                  leftIcon={<Icon as={BsTranslate} />}
                  variant="link"
                  onClick={onOpen}
                >
                  {change_language}
                </Button>
              </Flex>
            </GridItem>
            <GridItem>
              <Heading fontSize={'xl'} color={headingColor}>
                {company}
              </Heading>
              <List fontWeight={'bold'} color={textColor} spacing={3} mt={4}>
                <ListItem>
                  <LocalizedLink to="/about-us">{navigation_about_us}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/cars">{navigation_cars}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/services">{navigation_services}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/news">{navigation_news}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <Link href="https://www.kleinanzeigen.de/pro/autocenter-meschede-gmbh" isExternal={true}>{tireShop}</Link>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/contact">{navigation_contact}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <Link href="https://join.com/companies/autocenter-meschede" isExternal>
                    {navigation_jobs}
                  </Link>
                </ListItem>
              </List>
            </GridItem>

            <GridItem>
              <Heading fontSize={'xl'} color={headingColor}>
                {social_media}
              </Heading>
              <List fontWeight={'bold'} color={textColor} spacing={3} mt={4}>
                <ListItem>
                  <Link href="https://www.instagram.com/autocentermeschede/" isExternal>
                    Instagram
                  </Link>
                </ListItem>
                <ListItem>
                  <Link href="https://www.tiktok.com/@autocentermeschede" isExternal>
                    TikTok
                  </Link>
                </ListItem>
                <ListItem>
                  <Link href="https://www.facebook.com/autocentermeschede" isExternal>
                    Facebook
                  </Link>
                </ListItem>
                <ListItem>
                  <Link href="https://www.youtube.com/channel/UCIl78OPcJf6aIxIB87v8-VQ" isExternal>
                    Youtube
                  </Link>
                </ListItem>
              </List>
            </GridItem>
            <GridItem colSpan={[2, null, 1]}>
              <Heading fontSize={'xl'} color={headingColor}>
                {information}
              </Heading>
              <List fontWeight={'bold'} color={textColor} spacing={3} mt={4}>
                <ListItem>
                  <LocalizedLink to="/imprint">{imprint}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/gewaehrleistung" overflowWrap="anywhere">{warrantyTitle}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/privacy-policy">{privacy_policy}</LocalizedLink>
                </ListItem>
                <ListItem>
                  <LocalizedLink to="/accessibility">{accessibility}</LocalizedLink>
                </ListItem>
              </List>
            </GridItem>
            {!hideNewsletterSignup && (
              <GridItem
                colSpan={[2, null, 4]}
                borderTop="1px solid"
                borderColor={dividerColor}
                pt={[6, null, 8]}
                mt={2}
              >
                <NewsletterSignup source="footer" variant="footer" />
              </GridItem>
            )}
          </Grid>
        </Box>
        <Box bgColor={lowerBg} py={6} pl={['8', null, null, '16']} pr={['8', null, '24']}>
          <Grid maxW={'8xl'} templateColumns={['1fr', null, '1fr 1fr 1fr']} mx={'auto'} alignItems={'center'} gap={4}>
            <Text fontSize={'12px'} color={lowerTextColor} mr={[null, null, 'auto']} justifySelf={'center'}>
              © {new Date().getFullYear()} Autocenter Meschede. All rights reserved.
            </Text>
            <GridItem justifySelf={'center'}>
              <ChakraLogoSingleGray width={'auto'} height={'24px'} alt={alt_logo_single} />
            </GridItem>
            <Text fontSize={'12px'} color={lowerTextColor} ml={[null, null, 'auto']} justifySelf={'center'}>
              Made with &#10084; by{' '}
              <Link href={'https://www.cobtech.de/'} isExternal>
                CobTech
              </Link>
            </Text>
          </Grid>
        </Box>
      </Box>

      <SelectLanguageModal isOpen={isOpen} onClose={onClose} locale={locale} />
    </>
  );
};

export const MemoizedFooter = React.memo(Footer);
